import { useCurrentFrame, useVideoConfig } from 'remotion'
import {
  OVERLAY_POSITION_STYLES,
  type TextOverlay,
} from '@/lib/clipUtils'
import {
  getPresetById,
  resolveOverlayAnimation,
  resolveOverlayTextShadow,
} from '@/lib/textPresets'
import {
  CHARACTER_ENTER_ANIMATIONS,
  computeOverlayAnimation,
} from '@/lib/overlayAnimation'
import { clamp01, mix, easeOutCubic, easeOutBack } from '@/lib/easing'
import { secondsToFrames } from '../utils/framemath'

import { loadFont as loadInter } from '@remotion/google-fonts/Inter'
import { loadFont as loadMontserrat } from '@remotion/google-fonts/Montserrat'
import { loadFont as loadOswald } from '@remotion/google-fonts/Oswald'
import { loadFont as loadPoppins } from '@remotion/google-fonts/Poppins'
import { loadFont as loadRaleway } from '@remotion/google-fonts/Raleway'
import { loadFont as loadRobotoMono } from '@remotion/google-fonts/RobotoMono'
import { loadFont as loadBangers } from '@remotion/google-fonts/Bangers'
import { loadFont as loadPlayfair } from '@remotion/google-fonts/PlayfairDisplay'
import { loadFont as loadBebasNeue } from '@remotion/google-fonts/BebasNeue'
import { loadFont as loadJetBrainsMono } from '@remotion/google-fonts/JetBrainsMono'
import { loadFont as loadDMSerifDisplay } from '@remotion/google-fonts/DMSerifDisplay'
import { loadFont as loadCrimsonText } from '@remotion/google-fonts/CrimsonText'
import { loadFont as loadSpaceMono } from '@remotion/google-fonts/SpaceMono'
import { loadFont as loadOutfit } from '@remotion/google-fonts/Outfit'
import { loadFont as loadCormorantGaramond } from '@remotion/google-fonts/CormorantGaramond'
import { loadFont as loadCinzel } from '@remotion/google-fonts/Cinzel'
import { loadFont as loadBricolage } from '@remotion/google-fonts/BricolageGrotesque'
import { loadFont as loadLibreBodoni } from '@remotion/google-fonts/LibreBodoni'
import { loadFont as loadAbrilFatface } from '@remotion/google-fonts/AbrilFatface'
import { loadFont as loadOrbitron } from '@remotion/google-fonts/Orbitron'
import { loadFont as loadBarlowCondensed } from '@remotion/google-fonts/BarlowCondensed'
import { loadFont as loadRighteous } from '@remotion/google-fonts/Righteous'

const f = (loader: any, weights?: string[]) => loader('normal', { subsets: ['latin'], ...(weights && { weights }) }).fontFamily as string

const FONT_MAP: Record<string, string> = {
  'Inter': f(loadInter, ['300','400','500','600','700','800']),
  'Montserrat': f(loadMontserrat, ['300','400','500','600','700','800']),
  'Oswald': f(loadOswald, ['300','400','500','600','700']),
  'Poppins': f(loadPoppins, ['300','400','500','600','700','800']),
  'Raleway': f(loadRaleway, ['300','400','500','600','700','800']),
  'RobotoMono': f(loadRobotoMono, ['300','400','500','600','700']),
  'Bangers': f(loadBangers),
  'Playfair Display': f(loadPlayfair, ['400','500','600','700','800']),
  'Bebas Neue': f(loadBebasNeue),
  'JetBrains Mono': f(loadJetBrainsMono, ['300','400','500','600','700']),
  'DM Serif Display': f(loadDMSerifDisplay),
  'Crimson Text': f(loadCrimsonText, ['400','600','700']),
  'Space Mono': f(loadSpaceMono),
  'Outfit': f(loadOutfit, ['300','400','500','600','700','800']),
  'Cormorant Garamond': f(loadCormorantGaramond, ['300','400','500','600','700']),
  'Cinzel': f(loadCinzel, ['400','700','900']),
  'Bricolage Grotesque': f(loadBricolage, ['400','500','600','700','800']),
  'Libre Bodoni': f(loadLibreBodoni, ['400','700']),
  'Abril Fatface': f(loadAbrilFatface),
  'Orbitron': f(loadOrbitron, ['400','500','600','700','800','900']),
  'Barlow Condensed': f(loadBarlowCondensed, ['400','500','600','700','800']),
  'Righteous': f(loadRighteous),
}

// Google fonts above are loaded latin-only, so emoji must fall through to a
// system emoji font (Noto Color Emoji on render workers, native fonts elsewhere).
const EMOJI_FALLBACK = "'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI Emoji'"

function resolveFont(name?: string): string {
  const base = name ? (FONT_MAP[name] || name) : FONT_MAP['Inter']
  return `${base}, ${EMOJI_FALLBACK}`
}

const LEGACY_INLINE_STYLES: Record<string, React.CSSProperties> = {
  title: { fontSize: 72, fontWeight: 800, letterSpacing: '0.025em' },
  subtitle: { fontSize: 48, fontWeight: 500 },
  lower_third: { fontSize: 36, fontWeight: 500, backgroundColor: 'rgba(0,0,0,0.6)', padding: '12px 24px', borderRadius: 4 },
  caption: { fontSize: 32, backgroundColor: 'rgba(0,0,0,0.5)', padding: '10px 20px', borderRadius: 4 },
  credits: { fontSize: 42, fontWeight: 300, letterSpacing: '0.1em', textAlign: 'center', whiteSpace: 'pre-line' },
}

function computeWordStyle(
  wordAnimation: string | undefined,
  isActive: boolean,
  isPast: boolean,
  _wordIndex: number,
  _totalWords: number,
  currentTimeSec: number,
  wordStart: number,
  wordEnd: number,
  baseColor: string,
  highlightColor: string,
  overlay: TextOverlay,
  _frame: number,
): React.CSSProperties {
  const style: React.CSSProperties = {
    display: 'inline-block',
    transition: 'none',
  }

  // Eased entry for the active word: 0→1 over the first ~0.16s it's active, so
  // each word smoothly pops in instead of snapping between discrete states.
  const POP_WINDOW = 0.16
  const entry = isActive && wordEnd > wordStart
    ? clamp01((currentTimeSec - wordStart) / POP_WINDOW)
    : 0
  const back = easeOutBack(entry)

  switch (wordAnimation) {
    // Gentle eased pop + highlight bloom — the nuanced default.
    case 'pop':
    case 'scale': {
      style.color = isActive ? highlightColor : baseColor
      if (isActive) style.transform = `scale(${mix(1, 1.12, back)})`
      break
    }
    // Snappy overshoot from slightly small — YouTube punch.
    case 'punch': {
      style.color = isActive ? highlightColor : baseColor
      if (isActive) style.transform = `scale(${mix(0.9, 1.15, back)})`
      break
    }
    // Springy bounce-in from small — TikTok.
    case 'bounce': {
      style.color = isActive ? highlightColor : baseColor
      if (isActive) {
        const lift = mix(10, 0, easeOutCubic(entry))
        style.transform = `translateY(${lift}px) scale(${mix(0.7, 1.14, back)})`
      }
      break
    }
    // Active word sits in a bright rounded box with dark text — Hormozi.
    case 'box': {
      if (isActive) {
        style.color = '#0a0a0a'
        style.background = highlightColor
        style.padding = '0.02em 0.2em'
        style.borderRadius = '0.12em'
        style.transform = `scale(${mix(1, 1.06, back)})`
        style.WebkitBoxDecorationBreak = 'clone'
        style.boxDecorationBreak = 'clone'
      } else {
        style.color = baseColor
      }
      break
    }
    case 'spotlight': {
      style.color = isActive ? highlightColor : baseColor
      style.opacity = isActive ? 1 : isPast ? 0.45 : 0.22
      if (isActive) style.transform = `scale(${mix(1, 1.18, easeOutCubic(entry))})`
      break
    }
    case 'cascade': {
      style.color = isActive ? highlightColor : isPast ? baseColor : 'rgba(255,255,255,0.55)'
      if (isActive) style.transform = `scale(${mix(1, 1.08, back)}) translateY(${mix(0, -3, back)}px)`
      break
    }
    case 'none': {
      // Static subtitles — no per-word emphasis.
      style.color = baseColor
      break
    }
    case 'typewriter': {
      const isFutureWord = currentTimeSec < wordStart
      style.color = isFutureWord ? 'transparent' : (isActive ? highlightColor : baseColor)
      break
    }
    case 'fill': {
      if (isActive) {
        style.color = highlightColor
        style.WebkitTextStroke = undefined
      } else if (isPast) {
        style.color = highlightColor
      } else {
        style.color = 'transparent'
        if (overlay.stroke) {
          style.WebkitTextStroke = `${overlay.strokeWidth || 2}px ${overlay.stroke}`
        }
      }
      break
    }
    // One word at a time — only the active word is shown.
    case 'solo':
    case 'solo_pop': {
      if (isActive) {
        const duration = wordEnd - wordStart
        const elapsed = currentTimeSec - wordStart
        const progress = duration > 0 ? elapsed / duration : 0
        // 'solo' eases gently up; 'solo_pop' punches in from small with overshoot.
        let opacity = 1
        let scale = wordAnimation === 'solo_pop' ? mix(0.62, 1, back) : mix(0.9, 1, back)
        if (progress > 0.82) {
          const t = (progress - 0.82) / 0.18
          opacity = 1 - easeOutCubic(t)
          scale = wordAnimation === 'solo_pop' ? mix(1, 1.08, t) : mix(1, 0.92, t)
        }
        style.color = highlightColor || baseColor
        style.opacity = opacity
        style.transform = `scale(${scale})`
      } else {
        style.display = 'none'
      }
      break
    }
    default: {
      // Unknown animation → behave like the gentle eased pop.
      style.color = isActive ? highlightColor : baseColor
      if (isActive) style.transform = `scale(${mix(1, 1.1, back)})`
      break
    }
  }

  return style
}

const BLOCKED_CSS_PROPS = new Set(['position', 'z-index', 'zIndex', 'display', 'visibility', 'pointer-events', 'pointerEvents'])

function parseCustomCSS(css?: string): React.CSSProperties {
  if (!css) return {}
  const style: Record<string, string> = {}
  for (const decl of css.split(';')) {
    const colon = decl.indexOf(':')
    if (colon < 1) continue
    const prop = decl.slice(0, colon).trim()
    const val = decl.slice(colon + 1).trim()
    if (!val || BLOCKED_CSS_PROPS.has(prop)) continue
    const camel = prop.startsWith('-webkit-')
      ? 'webkit' + prop.slice(8).replace(/-([a-z])/g, (_, c) => c.toUpperCase()).replace(/^./, s => s.toUpperCase())
      : prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    if (BLOCKED_CSS_PROPS.has(camel)) continue
    style[camel] = val
  }
  return style as React.CSSProperties
}

function parseAccentBar(raw?: string): { side: string; color: string; width: number } | null {
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

function parseSubtitleConfig(raw?: string): { fontSize: number; fontWeight: number; color?: string; fontFamily?: string; letterSpacing?: string } | null {
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}

function WordHighlightOverlay({ overlay, frame, fps }: { overlay: TextOverlay; frame: number; fps: number }) {
  const words = overlay.words || []
  if (words.length === 0) return null

  const currentTimeSec = frame / fps
  const hasSpatialPos = overlay.spatialX != null || overlay.spatialY != null
  const posStyle = hasSpatialPos ? {
    left: `${overlay.spatialX ?? 0}%`,
    top: `${overlay.spatialY ?? 0}%`,
    ...(overlay.spatialWidth != null ? { width: `${overlay.spatialWidth}%` } : {}),
    ...(overlay.spatialHeight != null ? { height: `${overlay.spatialHeight}%` } : {}),
  } : (OVERLAY_POSITION_STYLES[overlay.position || 'bottom_center'] || OVERLAY_POSITION_STYLES.bottom_center)
  const baseColor = overlay.color || '#ffffff'
  const highlightColor = overlay.highlightColor || '#ffdd00'
  const bgColor = overlay.background || 'rgba(0,0,0,0.7)'
  const fontFam = resolveFont(overlay.fontFamily)
  const fontSize = overlay.fontSize || 48
  const fontWeight = overlay.fontWeight || 800
  const wordAnim = overlay.wordAnimation
  const isCascade = wordAnim === 'cascade'

  const baseShadow = overlay.shadow && overlay.shadow !== 'none' ? overlay.shadow : ''
  const textShadow = overlay.glow
    ? [baseShadow, overlay.glow].filter(Boolean).join(', ')
    : baseShadow || '0 2px 8px rgba(0,0,0,0.8), 0 0 4px rgba(0,0,0,0.5)'

  const combinedStyle: React.CSSProperties = {
    ...posStyle,
    fontFamily: fontFam,
    fontSize,
    fontWeight,
    position: 'absolute',
    color: baseColor,
    textShadow: textShadow === 'none' ? undefined : textShadow,
    pointerEvents: 'none',
    zIndex: 30,
    userSelect: 'none',
    backgroundColor: bgColor === 'transparent' ? undefined : bgColor,
    padding: '8px 20px',
    borderRadius: 8,
    lineHeight: isCascade ? 1.6 : 1.4,
    maxWidth: '85%',
    textAlign: 'center',
    letterSpacing: overlay.letterSpacing || undefined,
    display: 'flex',
    flexWrap: isCascade ? undefined : 'wrap',
    gap: isCascade ? undefined : '0.36em',
    flexDirection: isCascade ? 'column' : undefined,
    alignItems: 'center',
    justifyContent: isCascade ? undefined : 'center',
  }

  if (overlay.stroke && wordAnim !== 'fill') {
    combinedStyle.WebkitTextStroke = `${overlay.strokeWidth || 2}px ${overlay.stroke}`
    // Paint the stroke BEHIND the fill so the outline doesn't erode the letters.
    combinedStyle.paintOrder = 'stroke'
  }

  return (
    <div style={combinedStyle}>
      {words.map((word, i) => {
        const isActive = currentTimeSec >= word.s && currentTimeSec < word.e
        const isPast = currentTimeSec >= word.e

        const wStyle = computeWordStyle(
          wordAnim, isActive, isPast, i, words.length,
          currentTimeSec, word.s, word.e,
          baseColor, highlightColor, overlay, frame,
        )

        if (isCascade) {
          return (
            <span key={i} style={wStyle}>
              {word.w}
            </span>
          )
        }

        return (
          <span key={i} style={wStyle}>
            {word.w}
          </span>
        )
      })}
    </div>
  )
}

function SingleTextOverlayCore({ overlay, frame, fps }: { overlay: TextOverlay; frame: number; fps: number }) {
  const text = overlay.text.replace(/\\n/g, '\n')

  const startFrame = secondsToFrames(overlay.startTime, fps)
  const durationFrames = secondsToFrames(overlay.duration, fps)
  const endFrame = startFrame + durationFrames

  if (frame < startFrame || frame > endFrame) return null

  if (overlay.words && overlay.words.length > 0) {
    return <WordHighlightOverlay overlay={overlay} frame={frame} fps={fps} />
  }

  const preset = overlay.preset ? getPresetById(overlay.preset) : null
  const isPresetOverlay = !!preset || !!overlay.fontFamily

  const progress = overlay.duration > 0 ? (frame - startFrame) / durationFrames : 0
  const hasSpatialPos = overlay.spatialX != null || overlay.spatialY != null
  const posStyle = hasSpatialPos ? {
    left: `${overlay.spatialX ?? 0}%`,
    top: `${overlay.spatialY ?? 0}%`,
    ...(overlay.spatialWidth != null ? { width: `${overlay.spatialWidth}%` } : {}),
    ...(overlay.spatialHeight != null ? { height: `${overlay.spatialHeight}%` } : {}),
  } as React.CSSProperties : (OVERLAY_POSITION_STYLES[overlay.position || 'center'] || OVERLAY_POSITION_STYLES.center)
  const anim = computeOverlayAnimation(overlay, progress, frame, fps)

  const spatialRotation = overlay.spatialRotation ? `rotate(${overlay.spatialRotation}deg)` : ''
  const composedTransform = [posStyle.transform, anim.transform, spatialRotation].filter(Boolean).join(' ') || undefined

  let combinedStyle: React.CSSProperties

  if (isPresetOverlay) {
    const ff = resolveFont(overlay.fontFamily || preset?.fontFamily)
    const fs = overlay.fontSize || preset?.fontSize || 48
    const fw = overlay.fontWeight || preset?.fontWeight || 400
    const bg = overlay.background || preset?.background
    const pad = overlay.padding || preset?.padding
    const br = overlay.borderRadius ?? preset?.borderRadius
    const mw = overlay.maxWidth || preset?.maxWidth

    combinedStyle = {
      ...posStyle,
      fontFamily: ff,
      fontSize: fs,
      fontWeight: fw,
      letterSpacing: overlay.letterSpacing || preset?.letterSpacing,
      textTransform: (overlay.textTransform || preset?.textTransform) as any,
      fontStyle: (overlay.fontStyle || preset?.fontStyle) as any,
      opacity: anim.opacity,
      transform: composedTransform,
      filter: anim.filter,
      position: 'absolute',
      color: overlay.color || '#fff',
      textShadow: resolveOverlayTextShadow(overlay, preset),
      pointerEvents: 'none',
      whiteSpace: 'pre-line',
      zIndex: 30,
      userSelect: 'none',
      lineHeight: 1.2,
    }

    if (bg) {
      if (/^(linear|radial)-gradient/.test(bg)) {
        combinedStyle.background = bg
      } else {
        combinedStyle.backgroundColor = bg
      }
    }
    if (pad) combinedStyle.padding = pad
    if (br !== undefined) combinedStyle.borderRadius = br
    if (mw) combinedStyle.maxWidth = mw

    if (overlay.glowColor || preset?.glowColor) {
      const gc = overlay.glowColor || preset!.glowColor!
      combinedStyle.textShadow = `0 0 12px ${gc}, 0 0 36px ${gc}, 0 0 72px ${gc}80, 0 2px 14px rgba(0,0,0,0.5)`
    }
    const stroke = overlay.textStroke || preset?.textStroke
    if (stroke) {
      combinedStyle.WebkitTextStroke = stroke
      combinedStyle.paintOrder = 'stroke'
    }
  } else {
    const legacyStyle = LEGACY_INLINE_STYLES[overlay.style || 'title'] || LEGACY_INLINE_STYLES.title
    combinedStyle = {
      ...posStyle,
      ...legacyStyle,
      fontFamily: resolveFont('Inter'),
      opacity: anim.opacity,
      transform: composedTransform,
      filter: anim.filter,
      position: 'absolute',
      color: overlay.color || '#fff',
      textShadow: resolveOverlayTextShadow(overlay, null),
      pointerEvents: 'none',
      whiteSpace: 'pre-line',
      zIndex: 30,
      userSelect: 'none',
      ...(overlay.fontSize ? { fontSize: overlay.fontSize } : {}),
      ...(overlay.fontWeight ? { fontWeight: overlay.fontWeight } : {}),
      ...(overlay.letterSpacing ? { letterSpacing: overlay.letterSpacing } : {}),
      ...(overlay.background ? { backgroundColor: overlay.background, padding: '0.5em 1em', borderRadius: '0.25em' } : {}),
    }
    if (overlay.stroke) {
      combinedStyle.WebkitTextStroke = `${overlay.strokeWidth || 2}px ${overlay.stroke}`
      combinedStyle.paintOrder = 'stroke'
    }
  }

  const custom = parseCustomCSS(overlay.customCSS)
  if (Object.keys(custom).length > 0) Object.assign(combinedStyle, custom)

  const accentBar = parseAccentBar(overlay.accentBar || (preset?.accentBar ? JSON.stringify(preset.accentBar) : undefined))
  const subtitleCfg = parseSubtitleConfig(overlay.subtitleConfig || (preset?.subtitle ? JSON.stringify(preset.subtitle) : undefined))
  const showLineAbove = overlay.lineAbove ?? preset?.lineAbove
  const showLineBelow = overlay.lineBelow ?? preset?.lineBelow

  // Optional kicker/eyebrow: a small, wide-tracked label above the title.
  // Inherits the title's font + alignment; sized relative to the title so it
  // scales with it. Pairs with the lower_left/center title placements.
  const eyebrow = overlay.eyebrow?.trim()
  const eyebrowStyle: React.CSSProperties = {
    fontSize: '0.30em',
    fontWeight: 600,
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    opacity: 0.72,
    marginBottom: '0.7em',
    lineHeight: 1.2,
  }

  const effAnim = resolveOverlayAnimation(overlay)
  const activeEnter = effAnim.enterAnimation || effAnim.animation || ''
  const isCharAnim = CHARACTER_ENTER_ANIMATIONS.has(activeEnter)

  const renderText = (textContent: string) => {
    if (isCharAnim && anim.textSlice !== undefined) {
      const sliceCount = Math.round((anim.textSlice / 100) * textContent.length)
      return (
        <>
          <span>{textContent.slice(0, sliceCount)}</span>
          <span style={{ opacity: 0 }}>{textContent.slice(sliceCount)}</span>
        </>
      )
    }
    if (isCharAnim && anim.perCharStyle) {
      const chars = textContent.split('')
      const visibleCount = chars.filter(c => c !== '\n').length
      let visibleIndex = 0
      return (
        <>
          {chars.map((char, i) => {
            if (char === '\n') return <br key={i} />
            const idx = visibleIndex++
            return (
              <span key={i} style={anim.perCharStyle!(idx, visibleCount)}>
                {char === ' ' ? '\u00A0' : char}
              </span>
            )
          })}
        </>
      )
    }
    return textContent
  }

  const hasDecorations = accentBar || subtitleCfg || showLineAbove || showLineBelow

  if (hasDecorations) {
    return (
      <div style={combinedStyle}>
        <div style={{ display: 'flex', position: 'relative' }}>
          {accentBar && (
            <div
              style={{
                position: 'absolute',
                left: accentBar.side === 'left' ? -accentBar.width * 3 : undefined,
                right: accentBar.side !== 'left' ? -accentBar.width * 3 : undefined,
                top: 0,
                bottom: 0,
                width: accentBar.width,
                backgroundColor: accentBar.color,
                borderRadius: accentBar.width / 2,
              }}
            />
          )}
          <div>
            {showLineAbove && (
              <div style={{ width: 60, height: 2, backgroundColor: 'rgba(255,255,255,0.4)', marginBottom: 12 }} />
            )}
            {eyebrow && <div style={eyebrowStyle}>{eyebrow}</div>}
            <div>{renderText(text)}</div>
            {overlay.subtitleText && subtitleCfg && (
              <div style={{
                fontFamily: resolveFont(subtitleCfg.fontFamily || overlay.fontFamily || preset?.fontFamily),
                fontSize: subtitleCfg.fontSize,
                fontWeight: subtitleCfg.fontWeight,
                color: subtitleCfg.color || 'rgba(255,255,255,0.7)',
                letterSpacing: subtitleCfg.letterSpacing,
                marginTop: 4,
              }}>
                {overlay.subtitleText}
              </div>
            )}
            {showLineBelow && (
              <div style={{ width: 60, height: 2, backgroundColor: 'rgba(255,255,255,0.4)', marginTop: 12 }} />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (eyebrow) {
    return (
      <div style={combinedStyle}>
        <div style={eyebrowStyle}>{eyebrow}</div>
        <div>{renderText(text)}</div>
      </div>
    )
  }

  return (
    <div style={combinedStyle}>
      {renderText(text)}
    </div>
  )
}

/** Remotion-aware wrapper — reads frame/fps from Remotion context */
function SingleTextOverlay({ overlay }: { overlay: TextOverlay }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  return <SingleTextOverlayCore overlay={overlay} frame={frame} fps={fps} />
}

/** Used inside Remotion Player compositions */
export function TextOverlayRenderer({ overlays }: { overlays: TextOverlay[] }) {
  if (!overlays.length) return null
  return (
    <>
      {overlays.map(overlay => (
        <SingleTextOverlay key={overlay.id} overlay={overlay} />
      ))}
    </>
  )
}

/** Standalone renderer for use outside Remotion Player (e.g. above R3F effects layer) */
export function StandaloneTextOverlayRenderer({ overlays, frame, fps }: { overlays: TextOverlay[]; frame: number; fps: number }) {
  if (!overlays.length) return null
  return (
    <>
      {overlays.map(overlay => (
        <SingleTextOverlayCore key={overlay.id} overlay={overlay} frame={frame} fps={fps} />
      ))}
    </>
  )
}
